import { useState, useEffect, useMemo, useCallback } from 'react'
import { useChat } from '../contexts/ChatContext'
import { ContextMenu, ConfirmDialog, Modal, avatarColorIndex, LibraryDrawer, LibraryRow, CollectionDrawer, SelectionBar, useListSelection } from '@ofself/shell'
import { ALL as ALL_WORLDS, inWorld, worldName } from '../utils/worlds'
import { listGroupMembers } from '../services/api'
import useWorldMove from './useWorldMove'
import { formatShortTime } from '../utils/time'
import { dmDisplayName } from '../utils/convoName'

// THE kit hash (one hue per name, stable across chrome + content — see
// APP_GUIDELINES §4); the app's --avatar-N aliases map onto the kit palette.
const hashColor = (name) => `var(--avatar-${avatarColorIndex(name)})`

const COLLAPSED_KEY = 'paradigm_sidebar_collapsed'

function loadCollapsed() {
  try {
    const stored = localStorage.getItem(COLLAPSED_KEY)
    return stored ? JSON.parse(stored) : {}
  } catch { return {} }
}

function saveCollapsed(state) {
  try { localStorage.setItem(COLLAPSED_KEY, JSON.stringify(state)) } catch {}
}

export default function Sidebar({ onSelectChannel, onOpenAtrium }) {
  const {
    dmChannels, channels, currentUser, createDM, selectChannel: _selectChannel, activeChannel,
    unreadCounts, groups, activeGroup, selectGroup, groupChannelsByGroup,
    createGroupChannel, lastMessageMap, members,
    mutedChannels, toggleChannelMute,
    pinnedChannels, toggleChannelPin,
    activeHuddles, markAsUnread,
    composingDM,
    startComposeChannel, startComposeDM,
    leaveChannel, deleteChannel, renameChannel,
    worldsByGroup, activeWorld, enterWorld, createWorld, placeChannel,
  } = useChat()
  const worlds = (activeGroup && worldsByGroup[activeGroup.id]) || []
  const [worldsOpen, setWorldsOpen] = useState(false)
  const selectChannel = onSelectChannel || _selectChannel

  // MESSAGES LIST density — 'compact' (Slack-like: hashtag/avatar + name only) vs 'detailed'
  // (last-message previews + timestamps). App setting (SettingsPage), default compact; live-syncs.
  const [compact, setCompact] = useState(() => localStorage.getItem('paradigm_msg_list_compact') !== 'false')
  useEffect(() => {
    const sync = () => setCompact(localStorage.getItem('paradigm_msg_list_compact') !== 'false')
    window.addEventListener('paradigm-msg-list-compact', sync)
    return () => window.removeEventListener('paradigm-msg-list-compact', sync)
  }, [])
  const [sidebarView, setSidebarView] = useState('messages') // 'messages' | 'activity'
  const [activeFilter, setActiveFilter] = useState('All')
  const [collapsedSections, setCollapsedSections] = useState(loadCollapsed)
  // THE FILTER LIVES IN THE DRAWER HEAD (shell 0.206.0, owner call
  // 2026-09-15): one query per family, held here, narrowing names only.
  // (Full-text search — people, message content — is the railbar's.)
  const [drawerFilters, setDrawerFilters] = useState({})
  const drawerFilterProps = (key) => ({
    filter: drawerFilters[key] || '',
    onFilter: (q) => setDrawerFilters((f) => ({ ...f, [key]: q })),
  })
  const narrow = (key, rows) => {
    const q = (drawerFilters[key] || '').trim().toLowerCase()
    return q ? rows.filter((c) => (c._name || '').toLowerCase().includes(q)) : rows
  }
  const [inlineCreateGroupId, setInlineCreateGroupId] = useState(null)
  const [newChannelName, setNewChannelName] = useState('')
  const [creating, setCreating] = useState(false)
  const [channelCreateStep, setChannelCreateStep] = useState('name') // 'name' | 'members'
  const [availableGroupMembers, setAvailableGroupMembers] = useState([])
  const [selectedMemberIds, setSelectedMemberIds] = useState([])
  const [createError, setCreateError] = useState('')
  const [confirmDeleteChannel, setConfirmDeleteChannel] = useState(null) // channelId to confirm delete
  const [renamingChannel, setRenamingChannel] = useState(null) // { id, name } — channel being renamed
  const [renameValue, setRenameValue] = useState('')

  // (right-click menu is now the shell <ContextMenu> per convo row — no manual state)

  // Persist collapsed state
  useEffect(() => { saveCollapsed(collapsedSections) }, [collapsedSections])

  const toggleSection = useCallback((key) => {
    setCollapsedSections(prev => ({ ...prev, [key]: !prev[key] }))
  }, [])

  // Inline channel create
  function handleOpenInlineCreate(e, groupId) {
    e.stopPropagation()
    if (collapsedSections[groupId]) {
      setCollapsedSections(prev => ({ ...prev, [groupId]: false }))
    }
    setInlineCreateGroupId(groupId)
    setNewChannelName('')
    setChannelCreateStep('name')
    setSelectedMemberIds([])
    setAvailableGroupMembers([])
    setCreateError('')
  }

  async function handleNameSubmit(e, groupId) {
    e.preventDefault()
    const name = newChannelName.trim().toLowerCase().replace(/\s+/g, '-')
    if (!name) return
    // Move to member selection step
    try {
      const res = await listGroupMembers(groupId)
      const members = (res.members || []).filter(m => {
        const uid = m.user_id || m.user?.id
        return uid !== currentUser?.id
      })
      setAvailableGroupMembers(members)
      setChannelCreateStep('members')
    } catch (err) {
      console.error('Failed to load group members:', err)
      // Fallback: create without member selection
      setCreating(true)
      try {
        await createGroupChannel(groupId, name)
        setInlineCreateGroupId(null)
        setNewChannelName('')
      } finally { setCreating(false) }
    }
  }

  function toggleMemberSelection(userId) {
    setSelectedMemberIds(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    )
  }

  async function handleCreateWithMembers(groupId) {
    const name = newChannelName.trim().toLowerCase().replace(/\s+/g, '-')
    if (!name) return
    setCreating(true)
    setCreateError('')
    try {
      await createGroupChannel(groupId, name, '', selectedMemberIds)
      setInlineCreateGroupId(null)
      setNewChannelName('')
      setChannelCreateStep('name')
      setSelectedMemberIds([])
    } catch (err) {
      const msg = err?.message || 'Failed to create channel'
      if (msg.toLowerCase().includes('already exists')) {
        setCreateError(`#${name} already exists in this group`)
      } else {
        setCreateError(msg)
      }
    } finally {
      setCreating(false)
    }
  }

  // Filtered DMs
  const filteredDMs = useMemo(() => {
    let dms = dmChannels.map(dm => {
      const name = dmDisplayName(dm, dm.other_user_id?.slice(0, 8) || dm.name || 'DM')
      return {
        ...dm,
        _type: dm.is_persona_dm ? 'persona_dm' : dm.is_plugin_dm ? 'plugin_dm' : 'dm',
        _name: name,
        _initial: (name[0] || '?').toUpperCase(),
        _avatarColor: '#A5B59A',
      }
    })

    if (activeFilter === 'Unread') {
      dms = dms.filter(d => !!unreadCounts[d.id])
    }

    // Sort by last activity
    dms.sort((a, b) => {
      const tA = lastMessageMap[a.id]?.timestamp
      const tB = lastMessageMap[b.id]?.timestamp
      if (!tA && !tB) return 0
      if (!tA) return 1
      if (!tB) return -1
      return new Date(tB) - new Date(tA)
    })

    return dms
  }, [dmChannels, activeFilter, unreadCounts, lastMessageMap])

  // Filtered group sections
  const filteredGroupSections = useMemo(() => {
    return groups.map(group => {
      let chs = (groupChannelsByGroup[group.id] || []).map(ch => ({
        ...ch,
        _type: 'channel',
        _name: ch.name,
        _initial: '#',
        _avatarColor: null,
      }))

      if (activeFilter === 'Unread') {
        chs = chs.filter(c => !!unreadCounts[c.id])
      }

      // Sort by last activity
      chs.sort((a, b) => {
        const tA = lastMessageMap[a.id]?.timestamp
        const tB = lastMessageMap[b.id]?.timestamp
        if (!tA && !tB) return 0
        if (!tA) return 1
        if (!tB) return -1
        return new Date(tB) - new Date(tA)
      })

      return { group, channels: chs }
    })
  }, [groups, groupChannelsByGroup, activeFilter, unreadCounts, lastMessageMap])

  // Unread counts per section (for collapsed headers)
  const sectionUnreadCounts = useMemo(() => {
    const counts = { dm: 0 }
    dmChannels.forEach(dm => { if (unreadCounts[dm.id]) counts.dm++ })
    groups.forEach(g => {
      counts[g.id] = 0
      ;(groupChannelsByGroup[g.id] || []).forEach(ch => { if (unreadCounts[ch.id]) counts[g.id]++ })
    })
    return counts
  }, [dmChannels, groups, groupChannelsByGroup, unreadCounts])

  // a group's channels, through the world you're in (utils/worlds.js: a lens, not a container)
  const allGroupChs = activeGroup
    ? (filteredGroupSections.find(s => s.group.id === activeGroup.id)?.channels || [])
    : []
  const groupChs = allGroupChs.filter(c => inWorld(c, activeWorld, worlds))
  const worldCount = id => allGroupChs.filter(c => inWorld(c, id, worlds)).length
  // ONE SPACE AT A TIME (owner call 2026-09-30: no conflation): a group
  // shows its channels; Personal shows your DMs. DMs belong to no group.
  const spaceDMs = activeGroup ? [] : filteredDMs
  const pinned = [...groupChs, ...spaceDMs].filter(c => pinnedChannels?.has(c.id))
  const shownPinned = narrow('pinned', pinned)
  const shownChs = narrow('channels', groupChs)
  const shownDMs = narrow('dms', spaceDMs)

  /* THE SELECTION (2026-10-03, the shell's useListSelection): Finder's gestures
     over a group's channels: ⌘-click toggles, ⇧-click takes the run, a press in
     empty space draws the marquee, ⌘A / Esc / ⇧⌘M while the list has the
     keyboard, and a selected row drags the whole selection onto a world. The
     rows in the order shown (a shut drawer's are not shown); DMs move nowhere. */
  const rowIds = activeGroup
    ? [...new Set([...(collapsedSections.pinned ? [] : shownPinned), ...(collapsedSections.channels ? [] : shownChs)].map(c => c.id))]
    : []
  const sel = useListSelection({ ids: rowIds, current: activeChannel?.id ?? null,
    name: id => `#${allGroupChs.find(c => c.id === id)?.name || 'channel'}`,
    onMove: (ids, anchor) => mover.ask(ids, anchor), disabled: !rowIds.length })
  const mover = useWorldMove(sel)

  // Right-click menu (shell <ContextMenu>) — built per-convo from its state.
  function buildCtxItems(convo) {
    const isMuted = mutedChannels?.has(convo.id)
    const isPinned = pinnedChannels?.has(convo.id)
    const canRename = (convo._type === 'channel' && convo.created_by === currentUser?.id) ||
      (convo._type === 'dm' && convo.is_group_dm && (convo.member_usernames?.length ?? 0) >= 2)
    return [
      { label: 'Mark as unread', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 13V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h9" /><polyline points="22,6 12,13 2,6" /></svg>, onSelect: () => markAsUnread(convo.id, null) },
      { label: isPinned ? 'Unpin' : 'Pin', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: 'rotate(45deg)' }}><line x1="12" y1="17" x2="12" y2="22" /><path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z" /></svg>, onSelect: () => toggleChannelPin(convo.id) },
      { label: isMuted ? 'Unmute' : 'Mute', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 5L6 9H2v6h4l5 4V5z" />{isMuted ? <><path d="M19.07 4.93a10 10 0 0 1 0 14.14" /><path d="M15.54 8.46a5 5 0 0 1 0 7.07" /></> : <><line x1="23" y1="9" x2="17" y2="15" /><line x1="17" y1="9" x2="23" y2="15" /></>}</svg>, onSelect: () => toggleChannelMute(convo.id) },
      convo.is_dm && { label: 'Delete conversation', danger: true, icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>, onSelect: () => leaveChannel(convo.id) },
      canRename && { label: `Rename ${convo.is_group_dm ? 'group chat' : 'channel'}`, icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>, onSelect: () => {
        const displayName = convo.is_group_dm ? (convo.other_username || convo.name || '') : (convo.name || '')
        setRenamingChannel({ id: convo.id, name: displayName }); setRenameValue(displayName)
      } },
      // A TAG'S THREE VERBS (useWorldMove), for the selection this row is in, else the row:
      // MOVE TO… and ADD TO… (the shell's pickers), REMOVE FROM the world you're in
      ...(convo._type === 'channel' && convo.group_id ? (() => {
        const ids = sel.idsFor(convo.id)
        const many = ids.length > 1 ? ` ${ids.length} channels` : ''
        return [
          { label: `Move${many} to…`, onSelect: () => mover.ask(ids) },
          { label: `Add${many} to…`, onSelect: () => mover.add.ask(ids) },
          mover.remove && { label: `Remove${many} from “${mover.world.name}”`, onSelect: () => mover.remove(ids) },
        ]
      })() : []),
      // THE WORLDS it stands in (a lens: as many as you check)
      convo._type === 'channel' && convo.group_id && worlds.length > 0 && { label: 'Worlds', items: worlds.map(w => {
        const on = (convo.world_ids || []).includes(w.id)
        return { label: w.name, checked: on, onSelect: () => placeChannel(convo, on ? { remove: [w.id] } : { add: [w.id] }) }
      }) },
      convo._type === 'channel' && { label: 'Leave channel', danger: true, icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>, onSelect: () => leaveChannel(convo.id) },
      (convo._type === 'channel' && convo.created_by === currentUser?.id) && { label: 'Delete channel for everyone', danger: true, icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>, onSelect: () => setConfirmDeleteChannel(convo.id) },
    ]
  }

  // THE DRAWER ROW (shell library grammar, 2026-08-09): one thin mono row —
  // the name yields, the meta never breaks. Unreads ride the meta slot (the
  // count; a bare dot for boolean unreads); read rows carry the short time.
  // Previews and avatar discs left the persistent list — summary is content,
  // breakdown is attention (PHILOSOPHY.md); the click is the breakdown.
  const renderDrawerRow = (convo) => {
    const isActive = activeChannel?.id === convo.id
    const unread = unreadCounts[convo.id]
    const isMuted = mutedChannels?.has(convo.id)
    const lastMsg = lastMessageMap[convo.id]
    const meta = unread
      ? (typeof unread === 'number' ? (unread > 99 ? '99+' : String(unread)) : '●')
      : (lastMsg?.timestamp ? formatShortTime(lastMsg.timestamp) : null)
    const open = () => selectChannel(convo)
    const rowEl = (
      <LibraryRow
        {...(rowIds.includes(convo.id) ? sel.rowProps(convo.id, { onClick: open }) : { onClick: open })}
        active={isActive}
        className={`${unread ? 'is-unread' : ''} ${isMuted ? 'is-muted' : ''}`.trim()}
        /* Channels read as #name — the hash is PART of the name, flush
           against it (owner call: no icon column, no gap). Still its own
           span so it can sit a shade quieter than the name it introduces.
           DMs stay bare: the asymmetry IS the signal. */
        name={
          <>
            {convo._type === 'channel' && <span className="drawer-row-hash">#</span>}
            {convo._name}
            {convo._type === 'persona_dm' && <span className="drawer-row-tag">agent</span>}
            {convo._type === 'plugin_dm' && <span className="drawer-row-tag">plugin</span>}
            {activeHuddles[convo.id] && (
              <span className="huddle-active-indicator">
                <span className="huddle-active-dot" />
                live
              </span>
            )}
          </>
        }
        meta={meta}
      />
    )
    return <ContextMenu key={convo.id} trigger={rowEl} items={buildCtxItems(convo)} />
  }

  return (
    <aside
      className="sidebar"
      data-qh-title="Sidebar"
      data-qh-body="Your channels, direct messages, and navigation for this space."
    >
      {/* THE LIBRARY GRAMMAR (shell 0.107.3, refractions' left wall): the
          world you're in, then the family drawers, CHANNELS and DMS, both
          standing whatever the space (an empty family keeps its drawer; the
          empty body carries the add row). New conversations start from the
          rail's New (the list's doors moved onto the rail, as the Mac's). */}
      <div data-compact={compact ? '' : undefined}
        {...(rowIds.length ? sel.listProps : {})}
        className={`block__body block-scroll${rowIds.length ? ' ofself-marquee-host' : ''}${sel.marquee.marqueeing ? ' is-marqueeing' : ''}`}>
        {/* THE WORLD YOU'RE IN (owner call 2026-10-01), the shell's collection head:
            its name and count; opened, All channels, Unsorted and every world in the
            group's order (each a place carried channels land, springing open under
            them), ＋ New world, then the atrium */}
        {activeGroup && (
          <CollectionDrawer title={worldName(activeWorld, worlds)} count={worldCount(activeWorld)}
            open={worldsOpen} onToggle={() => setWorldsOpen(o => !o)} word="worlds"
            items={[{ id: ALL_WORLDS, name: worldName(ALL_WORLDS, worlds), count: worldCount(ALL_WORLDS), master: true, here: activeWorld === ALL_WORLDS },
              ...mover.rooms.map(r => ({ ...r, here: r.id === activeWorld }))]}
            onPick={id => { enterWorld(activeGroup.id, id); setWorldsOpen(false) }}
            onDrop={(id, ids) => mover.drop(id, ids)}
            kind="world" onMint={async name => { const w = await createWorld(activeGroup.id, name); if (w) enterWorld(activeGroup.id, w.id) }}
            atriumWord="Every world" onAtrium={() => { onOpenAtrium?.(); setWorldsOpen(false) }} />
        )}
        {pinned.length > 0 && (
          <LibraryDrawer
            name="pinned"
            count={shownPinned.length}
            open={!collapsedSections['pinned']}
            onToggle={() => toggleSection('pinned')}
            {...drawerFilterProps('pinned')}
          >
            {shownPinned.map(renderDrawerRow)}
          </LibraryDrawer>
        )}
        {activeGroup && (
          <LibraryDrawer
            name="channels"
            count={shownChs.length}
            open={!collapsedSections['channels']}
            onToggle={() => toggleSection('channels')}
            onAdd={() => startComposeChannel(activeGroup.id)}
            addLabel="new channel"
            {...drawerFilterProps('channels')}
          >
            {shownChs.map(renderDrawerRow)}
            {groupChs.length === 0 && (
              <LibraryRow
                className="is-add"
                name="add channels…"
                onClick={() => startComposeChannel(activeGroup.id)}
              />
            )}
          </LibraryDrawer>
        )}
        {!activeGroup && (
        <LibraryDrawer
          name="dms"
          count={shownDMs.length}
          open={!collapsedSections['dm']}
          onToggle={() => toggleSection('dm')}
          onAdd={() => startComposeDM()}
          addLabel="new dm"
          {...drawerFilterProps('dms')}
        >
          {shownDMs.map(renderDrawerRow)}
          {filteredDMs.length === 0 && (
            <LibraryRow
              className="is-add"
              name="add dms…"
              onClick={() => startComposeDM()}
            />
          )}
        </LibraryDrawer>
        )}
      </div>
      {/* THE SELECTION BAR at the column's foot (the shell's): MOVE TO…, ADD TO…, REMOVE FROM <the world in view>, CLEAR */}
      <SelectionBar count={sel.count} noun="channel" onClear={sel.clear} move={[mover, mover.add]} />

      <ConfirmDialog
        open={!!confirmDeleteChannel}
        title="Delete channel?"
        confirmLabel="Delete"
        danger
        onConfirm={() => { deleteChannel(confirmDeleteChannel); setConfirmDeleteChannel(null) }}
        onCancel={() => setConfirmDeleteChannel(null)}
      >
        This will permanently delete the channel and all its messages for everyone. This action cannot be undone.
      </ConfirmDialog>

      <Modal open={!!renamingChannel} onClose={() => setRenamingChannel(null)} title="Rename channel" size="md">
            <input
              type="text"
              className="modal-input"
              value={renameValue}
              onChange={e => setRenameValue(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && renameValue.trim() && renameValue.trim() !== renamingChannel?.name) {
                  renameChannel(renamingChannel.id, renameValue.trim())
                  setRenamingChannel(null)
                }
              }}
              autoFocus
              maxLength={100}
              placeholder="Channel name"
              style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border-secondary)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 'var(--ofself-text-base)', marginTop: 8 }}
            />
            <div className="modal-actions">
              <button className="modal-btn modal-btn-cancel" onClick={() => setRenamingChannel(null)}>Cancel</button>
              <button
                className="modal-btn modal-btn-primary"
                disabled={!renameValue.trim() || renameValue.trim() === renamingChannel?.name}
                onClick={() => {
                  renameChannel(renamingChannel.id, renameValue.trim())
                  setRenamingChannel(null)
                }}
              >Rename</button>
            </div>
      </Modal>

    </aside>
  )
}
